import { clearListCache, createKey, get } from "@/utils/networkCache";
import {
  SET_CHUNK,
  SET_ITEM,
  SET_ITEM_STATUS,
  SET_LIST,
  SET_LIST_STATUS,
  SET_PAGINATION,
  SET_SORT,
} from "@/store/commonMutations";
import records from "@/store/modules/dnsZones/records";
import { request } from "leatherman-js";
import Status from "@/classes/Status";

const CACHE_AUTHORITATIVE_KEY = "dns-zone-authoritative";
const CACHE_ITEM_KEY = "dns-zone";
const CACHE_LIST_KEY = "dns-zones";

const module = {
  namespaced: true,
  modules: {
    records,
  },
  state: {
    authoritative: null,
    authoritativeStatus: new Status(),
    chunk: null,
    item: null,
    itemStatus: new Status(),
    list: [],
    listStatus: new Status(),
    pagination: null,
    sort: null,
  },
  mutations: {
    SET_AUTHORITATIVE: (state, payload) => (state.authoritative = payload),
    SET_AUTHORITATIVE_STATUS: (state, status) => (state.authoritativeStatus.value = status),
    SET_CHUNK,
    SET_ITEM,
    SET_ITEM_STATUS,
    SET_LIST,
    SET_LIST_STATUS,
    SET_PAGINATION,
    SET_SORT,
  },
  actions: {
    add: (context, domain) => request.makeParse("addDnsZone", { domain }),
    addGoogleMailRecords: (context, id) => request.makeParse("addDnsZoneGoogleMailRecords", { id }),
    get: async ({ commit, state }, id) => {
      const newStatus =
        (state.itemStatus.isLoaded || state.itemStatus.isUpdating) && id === state.item?.id
          ? Status.UPDATING
          : Status.LOADING;
      commit("SET_ITEM_STATUS", newStatus);
      try {
        const response = await get(createKey(CACHE_ITEM_KEY, { id }), request.makeParse, [
          "getDnsZone",
          { id },
        ]);
        commit("SET_ITEM", response);
        commit("SET_ITEM_STATUS", Status.LOADED);
        return response;
      } catch (e) {
        commit("SET_ITEM_STATUS", Status.ERROR);
      }
    },
    getAuthoritative: async ({ commit, state }) => {
      const newStatus =
        state.authoritativeStatus.isLoaded || state.authoritativeStatus.isUpdating
          ? Status.UPDATING
          : Status.LOADING;
      commit("SET_AUTHORITATIVE_STATUS", newStatus);
      try {
        const response = await get(createKey(CACHE_AUTHORITATIVE_KEY), request.makeParse, [
          "getDnsZoneAuthoritative",
        ]);
        commit("SET_AUTHORITATIVE", response);
        commit("SET_AUTHORITATIVE_STATUS", Status.LOADED);
        return response;
      } catch (e) {
        commit("SET_AUTHORITATIVE_STATUS", Status.ERROR);
      }
    },
    getList: async ({ commit }, params) => {
      commit("SET_LIST_STATUS", Status.LOADING);
      commit("SET_CHUNK", params);
      commit("SET_SORT", params);
      try {
        const response = await get(createKey(CACHE_LIST_KEY, params), request.makeParse, [
          "getDnsZones",
          params,
        ]);
        commit("SET_LIST", response.list);
        commit("SET_PAGINATION", response.pagination);
        commit("SET_LIST_STATUS", Status.LOADED);
        return response;
      } catch (e) {
        commit("SET_LIST_STATUS", Status.ERROR);
      }
    },
    delete: (context, id) => request.makeParse("deleteDnsZone", { id }),
    refreshList: async ({ commit, state }) => {
      clearListCache(CACHE_LIST_KEY);
      const params = {
        pageIndex: state.chunk?.pagination?.index,
        pageSize: state.chunk?.pagination?.size,
        sortProperty: state.chunk?.sort?.property,
        sortDirection: state.chunk?.sort?.direction,
      };
      const newStatus =
        state.listStatus.isLoaded || state.listStatus.isUpdating ? Status.UPDATING : Status.LOADING;
      commit("SET_LIST_STATUS", newStatus);
      commit("SET_CHUNK", params);
      commit("SET_SORT", params);
      try {
        const response = await get(createKey(CACHE_LIST_KEY, params), request.makeParse, [
          "getDnsZones",
          params,
        ]);
        commit("SET_LIST", response.list);
        commit("SET_PAGINATION", response.pagination);
        commit("SET_LIST_STATUS", Status.LOADED);
        return response;
      } catch (e) {
        commit("SET_LIST_STATUS", Status.ERROR);
      }
    },
  },
  getters: {
    getAuthoritative: (state) => state.authoritative,
    getAuthoritativeStatus: (state) => state.authoritativeStatus,
    getItem: (state) => state.item,
    getItemStatus: (state) => state.itemStatus,
    getList: (state) => state.list,
    getListStatus: (state) => state.listStatus,
    getPagination: (state) => state.pagination,
    getSort: (state) => state.sort,
  },
};

export default module;
